import React, { useState, useEffect, useCallback } from "react";
import UsersTemplate from "components/templates/UsersTemplate";
import UserInfo from "components/templates/UsersTemplate/UserInfo";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { User, UserRole } from "common/models/types";
import { fetchUsers, fetchUserById, saveUser, createBulkUsers } from "api/userService";
import Spinner from "components/atoms/Spinner";
import Text from "components/atoms/Text";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { log } from "utils/LogUtil";
import { LogLevel } from "utils/LogUtil";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import { adminChangePassword, changePassword } from "api/authService";

const LIMIT = 20; // 한 번에 로드할 항목 수
const SEARCH_LOAD_LIMIT = 100000; // 검색 시 남은 전체를 한 번에 로드 (수천 명 규모 가정)

// 스피너 컨테이너
const SpinnerContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 1000;
`;

// 에러 컨테이너
const ErrorContainer = styled.div`
  text-align: center;
  max-width: 600px;
  margin: 100px auto;
  padding: 20px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

// 페이지 컨테이너
const PageContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const Users: React.FC = () => {
  const navigate = useNavigate();
  // 사용자 데이터 상태
  const [users, setUsers] = useState<User[]>([]);
  // 선택된 사용자 상태
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  // 현재 역할 필터
  const [currentRole, setCurrentRole] = useState<string | undefined>(undefined);
  // 로딩 상태
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // 에러 상태
  const [error, setError] = useState<string | null>(null);
  // 더 불러올 수 있는지 여부
  const [hasMore, setHasMore] = useState<boolean>(true);
  // 더 로드 중인지 여부
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  // 상세 정보 로딩 중 여부
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  // 페이지 번호
  const [page, setPage] = useState<number>(1);

  // 현재 로그인한 사용자 정보
  const { user } = useSelector((state: RootState) => state.user);

  // 사용자 목록 가져오기
  const loadUsers = useCallback(async (pageToLoad = 1, roleToFilter?: string) => {
    try {
      const isInitialLoad = pageToLoad === 1;
      if (isInitialLoad) {
        setIsLoading(true);
        // 초기 로드시 선택된 사용자 초기화
        setSelectedUser(null);

        // 초기 로드 시에만 대기 팝업 표시
        WaitingManager.show({
          message: "사용자 목록을 불러오는 중입니다...",
        });
      } else {
        setIsLoadingMore(true);
      }

      // 사용자 목록 가져오기
      const offset = (pageToLoad - 1) * LIMIT;
      const response = await fetchUsers({
        offset,
        limit: LIMIT,
        role: roleToFilter,
      });

      const loadedUsers = response.data || [];

      if (isInitialLoad) {
        setUsers(loadedUsers);
      } else {
        setUsers((prev) => [...prev, ...loadedUsers]);
      }

      // 더 불러올 데이터가 있는지 확인
      setHasMore(loadedUsers.length === LIMIT);
      setPage(pageToLoad);
    } catch (err) {
      log(LogLevel.ERROR, "사용자 목록 로드 오류:", err);
      setError("사용자 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);

      // 로딩이 완료되면 대기 팝업 숨기기
      WaitingManager.hide();
    }
  }, []);

  // 컴포넌트 마운트 시 사용자 목록 로드
  useEffect(() => {
    loadUsers(1, currentRole);
  }, [loadUsers, currentRole]);

  // 역할 필터 핸들러
  const handleSearch = useCallback(
    (role?: string) => {
      // 현재 역할 상태 업데이트
      setCurrentRole(role);
      // 사용자 목록 다시 로드
      loadUsers(1, role);
    },
    [loadUsers]
  );

  // 더 불러오기 핸들러
  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      loadUsers(page + 1, currentRole);
    }
  }, [isLoadingMore, hasMore, page, loadUsers, currentRole]);

  // 검색 시 남은 전체를 한 번의 요청으로 로드 (20개씩 순차 요청 방지)
  const handleLoadAll = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      const response = await fetchUsers({
        offset: users.length,
        limit: SEARCH_LOAD_LIMIT,
        role: currentRole,
      });
      const loadedUsers = response.data || [];
      setUsers((prev) => [...prev, ...loadedUsers]);
      // 대량 로드 후에는 더 불러올 데이터가 없다고 간주
      setHasMore(false);
    } catch (err) {
      log(LogLevel.ERROR, "검색용 전체 사용자 로드 오류:", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, users.length, currentRole]);

  // 사용자 선택 핸들러 (테이블 행 클릭 시 SlideManager로 표시)
  const handleSelectUser = useCallback(
    async (user: User) => {
      // 이미 선택된 사용자를 다시 클릭하면 슬라이드 닫기
      if (selectedUser && selectedUser.id === user.id) {
        SlideManager.hide();
        setSelectedUser(null);
        return;
      }

      if (user.id) {
        try {
          setIsLoadingDetail(true);
          // 상세 정보 로드 시 대기 팝업 표시
          WaitingManager.show({
            message: "사용자 상세 정보를 불러오는 중입니다...",
          });

          // ID로 상세 정보 조회
          const detailUser = await fetchUserById(user.id);
          setSelectedUser(detailUser);
          
          // SlideManager로 UserInfo 표시
          SlideManager.show({
            body: (
              <UserInfo
                user={detailUser}
                onUpdate={handleUpdateUser}
                onSelect={handleSelectUserChange}
                onReload={handleReloadUser}
                isLoading={isLoadingDetail}
                onDelete={handleDeleteUser}
                onResetPassword={handleResetPassword}
              />
            ),
            width: "800px",
            direction: "right",
            needCloseButton: true,
            onClose: () => {
              setSelectedUser(null);
            }
          });
        } catch (error) {
          log(LogLevel.ERROR, "사용자 상세 정보 로드 실패:", error);
          // 오류가 발생해도 기본 정보는 표시
          setSelectedUser(user);
          
          SlideManager.show({
            body: (
              <UserInfo
                user={user}
                onUpdate={handleUpdateUser}
                onSelect={handleSelectUserChange}
                onReload={handleReloadUser}
                isLoading={isLoadingDetail}
                onDelete={handleDeleteUser}
                onResetPassword={handleResetPassword}
              />
            ),
            width: "800px",
            direction: "right",
            needCloseButton: true,
            onClose: () => {
              setSelectedUser(null);
            }
          });
        } finally {
          setIsLoadingDetail(false);
          // 대기 팝업 숨기기
          WaitingManager.hide();
        }
      } else {
        setSelectedUser(user);
        
        // 새 사용자 생성 시에도 SlideManager 사용
        SlideManager.show({
          body: (
            <UserInfo
              user={user}
              onUpdate={handleUpdateUser}
              onSelect={handleSelectUserChange}
              onReload={handleReloadUser}
              isLoading={isLoadingDetail}
              onDelete={handleDeleteUser}
              onResetPassword={handleResetPassword}
            />
          ),
          width: "800px",
          direction: "right",
          needCloseButton: true,
          onClose: () => {
            setSelectedUser(null);
          }
        });
      }
    },
    [selectedUser, isLoadingDetail]
  );

  // 사용자 업데이트 핸들러
  const handleUpdateUser = useCallback(
    (updatedUser: User) => {
      log(LogLevel.VERBOSE, "handleUpdateUser", updatedUser);
      // 기존 사용자 업데이트
      if (updatedUser.id) {
        // 목록에서 해당 사용자 정보 업데이트
        setUsers((prevUsers) => prevUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)));

        // 현재 선택된 사용자와 동일한 경우 선택된 사용자 정보도 업데이트
        if (selectedUser?.id === updatedUser.id) {
          setSelectedUser(updatedUser);
        }
      }
    },
    [selectedUser?.id]
  );

  // 사용자 선택 변경 핸들러
  const handleSelectUserChange = useCallback(
    async (userOrNull: User | null) => {
      // 선택 해제 (null인 경우)
      if (userOrNull === null) {
        setSelectedUser(null);
        SlideManager.hide();
        return;
      }

      // 새로 생성된 사용자인 경우 (id가 이전에 없었다가 생긴 경우)
      const isNewlyCreated = userOrNull.id && !selectedUser?.id;

      if (isNewlyCreated) {
        try {
          // 새로 생성된 사용자의 경우 목록을 다시 로드하고, 상세 정보도 다시 조회
          setIsLoadingDetail(true);
          await loadUsers(1, currentRole);

          // 서버에서 방금 생성된 사용자의 전체 정보 조회
          const newUserDetail = await fetchUserById(userOrNull.id);
          setSelectedUser(newUserDetail);

          // 성공 토스트 메시지 표시
          ToastManager.show({
            content: `${newUserDetail.name} 사용자가 생성되었습니다.`,
            duration: 3000,
          });
          
          // SlideManager 닫기 (새 사용자 생성 성공)
          SlideManager.hide();
        } catch (error) {
          log(LogLevel.ERROR, "새 사용자 상세 정보 로드 실패:", error);
          // 에러 발생 시 서버에서 받은 기본 정보로만 상태 업데이트
          setSelectedUser(userOrNull);

          // 에러 토스트 메시지 표시
          ToastManager.show({
            content: "사용자 정보를 완전히 불러오지 못했습니다.",
            duration: 3000,
          });
        } finally {
          setIsLoadingDetail(false);
        }
      } else {
        // 일반적인 선택 변경의 경우
        setSelectedUser(userOrNull);
      }
    },
    [loadUsers, selectedUser?.id, currentRole]
  );

  // 사용자 정보 새로고침 핸들러
  const handleReloadUser = useCallback(
    async (userId: string) => {
      try {
        setIsLoadingDetail(true);
        // 대기 팝업 표시
        WaitingManager.show({
          message: "사용자 정보를 새로고침하는 중입니다...",
        });

        // 사용자 목록 다시 로드
        await loadUsers(1, currentRole);
        // 해당 사용자를 다시 선택
        const reloadedUser = users.find((u) => u.id === userId) || null;
        setSelectedUser(reloadedUser);

        // 성공 토스트 메시지 표시
        if (reloadedUser) {
          ToastManager.show({
            content: `${reloadedUser.name} 사용자 정보가 새로고침되었습니다.`,
            duration: 3000,
          });
        } else {
          ToastManager.show({
            content: "사용자를 찾을 수 없습니다.",
            duration: 3000,
          });
        }
      } catch (error) {
        log(LogLevel.ERROR, "사용자 정보 새로고침 오류:", error);
        // 에러 토스트 메시지 표시
        ToastManager.show({
          content: "사용자 정보 새로고침에 실패했습니다.",
          duration: 3000,
        });
      } finally {
        setIsLoadingDetail(false);
        // 대기 팝업 숨기기
        WaitingManager.hide();
      }
    },
    [loadUsers, currentRole, users]
  );

  // 새 사용자 생성 핸들러
  const handleCreateUser = useCallback(() => {
    // 새 사용자 기본 템플릿
    const newUser: User = {
      id: "", // 새 사용자는 빈 id로 표시
      name: "",
      email: "",
      roles: [UserRole.USER], // 기본 역할은 일반 사용자
      createdAt: new Date(),
      license: "", // 라이센스 정보
      guidedVersion: "", // 가이드 버전
      agreementVersion: "", // 약관 동의 버전
    };

    // 새 사용자 선택
    setSelectedUser(newUser);
    
    // SlideManager로 새 사용자 생성 폼 표시
    SlideManager.show({
      body: (
        <UserInfo
          user={newUser}
          onUpdate={handleUpdateUser}
          onSelect={handleSelectUserChange}
          onReload={handleReloadUser}
          isLoading={false}
          onDelete={handleDeleteUser}
          onResetPassword={handleResetPassword}
        />
      ),
      width: "800px",
      direction: "right",
      needCloseButton: true,
      onClose: () => {
        setSelectedUser(null);
      }
    });
  }, []);

  const handleCreateBulkUser = useCallback(() => {
    // 파일 선택을 위한 input 엘리먼트 생성
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".xls,.xlsx,.csv,.tsv"; // 지원하는 파일 형식
    fileInput.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];

        try {
          // 대기 팝업 표시
          WaitingManager.show({
            message: "대량 사용자 생성을 처리 중입니다...",
          });

          // API 호출
          const response = await createBulkUsers(file);

          if (response.error === 0) {
            // 성공 시 결과 표시
            const resultData = response.data;
            let resultMessage = `총 ${
              (resultData?.successCount || 0) + (resultData?.failureCount || 0)
            }명의 사용자 처리 완료\n`;
            resultMessage += `성공: ${resultData?.successCount || 0}명\n`;
            resultMessage += `실패: ${resultData?.failureCount || 0}명\n`;

            if (resultData?.failures && resultData.failures.length > 0) {
              resultMessage += "\n실패한 항목:\n";
              resultData.failures.forEach((failure) => {
                resultMessage += `${failure.row}행: ${failure.data.name} (${failure.data.email})\n`;
                resultMessage += `  - 입력 데이터: ${failure.data.company}, ${failure.data.license}, ${failure.data.phone}\n`;
                resultMessage += `  - 실패 이유: ${failure.reason}\n\n`;
              });
            }

            ConfirmManager.show({
              title: "대량 사용자 생성 완료",
              body: (
                <div style={{ whiteSpace: "pre-line", fontFamily: "monospace", fontSize: "14px" }}>
                  {resultMessage}
                </div>
              ),
              buttons: [
                <Button
                  key="confirm"
                  buttonType="button"
                  color="Primary"
                  size="Medium"
                  type="Text"
                  variant="Contained"
                  onClick={() => {
                    ConfirmManager.hide();
                    // 사용자 목록 새로고침
                    loadUsers(1, currentRole);
                  }}
                >
                  확인
                </Button>,
              ],
            });

            ToastManager.show({
              content: "대량 사용자 생성이 완료되었습니다.",
              duration: 3000,
            });
          } else {
            // 실패 시 에러 메시지 표시
            ConfirmManager.show({
              title: "대량 사용자 생성 실패",
              body: response.message || "알 수 없는 오류가 발생했습니다.",
              buttons: [
                <Button
                  key="confirm"
                  buttonType="button"
                  color="Primary"
                  size="Medium"
                  type="Text"
                  variant="Contained"
                  onClick={() => {
                    ConfirmManager.hide();
                  }}
                >
                  확인
                </Button>,
              ],
            });
          }
        } catch (error) {
          log(LogLevel.ERROR, "대량 사용자 생성 오류:", error);
          ToastManager.show({
            content: "대량 사용자 생성에 실패했습니다.",
            duration: 3000,
          });
        } finally {
          // 대기 팝업 숨기기
          WaitingManager.hide();
        }
      }
    };

    // 파일 선택 다이얼로그 열기
    fileInput.click();
  }, [loadUsers, currentRole]);

  const handleResetPassword = useCallback((user: User) => {
    log(LogLevel.VERBOSE, "handleResetPassword", user);
    ConfirmManager.show({
      title: "비밀번호 변경",
      body: "비밀번호를 변경하시겠습니까?",
      buttons: [
        <Button
          size="Medium"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={async () => {
            ConfirmManager.hide();
            try {
              await adminChangePassword(user.id, "ymyd" + user.phone.slice(-4));
              ToastManager.show({
                content: "비밀번호가 변경되었습니다.",
              });
            } catch (error) {
              log(LogLevel.ERROR, "비밀번호 변경 오류:", error);
              ToastManager.show({
                content: "비밀번호 변경에 실패했습니다.",
              });
            }
          }}
        >
          확인
        </Button>,
      ],
    });
  }, []);

  // 사용자 삭제 핸들러
  const handleDeleteUser = useCallback(
    (userId: string) => {
      // 삭제된 사용자 정보 미리 저장 (토스트 메시지용)
      const deletedUser = users.find((user) => user.id === userId);

      // 로컬 상태에서 사용자 제거
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
      // 선택된 사용자가 삭제된 사용자인 경우 선택 해제 및 슬라이드 닫기
      if (selectedUser?.id === userId) {
        setSelectedUser(null);
        SlideManager.hide();
      }

      // 성공 토스트 메시지 표시
      if (deletedUser) {
        ToastManager.show({
          content: `${deletedUser.name} 사용자가 삭제되었습니다.`,
          duration: 3000,
        });
      } else {
        ToastManager.show({
          content: "사용자가 삭제되었습니다.",
          duration: 3000,
        });
      }
    },
    [selectedUser, users]
  );

  // 로딩 중인 경우 (첫 로드)
  if (isLoading && users.length === 0) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );
  }

  // 에러가 발생한 경우
  if (error) {
    return (
      <ErrorContainer>
        <Text textType="H3">오류가 발생했습니다</Text>
        <p>{error}</p>
        <Button
          buttonType="button"
          color="Primary"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={() => {
            setError(null);
            loadUsers(1, currentRole);
          }}
        >
          다시 시도
        </Button>
      </ErrorContainer>
    );
  }

  return (
    <PageContainer>
      <UsersTemplate
        users={users}
        selectedUser={selectedUser}
        onSelectUser={handleSelectUser}
        onUpdateUser={handleUpdateUser}
        onSelectUserChange={handleSelectUserChange}
        onReloadUser={handleReloadUser}
        onCreateUser={handleCreateUser}
        onCreateBulkUser={handleCreateBulkUser}
        onSearch={handleSearch}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
        onLoadAll={handleLoadAll}
        isLoadingDetail={isLoadingDetail}
        onDeleteUser={handleDeleteUser}
        onResetPassword={handleResetPassword}
      />
    </PageContainer>
  );
};

export default Users;
